import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import LiveCodingStats from '../portfolio/LiveCodingStats';

export default function TerminalTheme({ profileData, socials, activeColor }) {
  const { core, education, experience, projects, skills } = profileData;
  const [typedHeader, setTypedHeader] = useState('');
  const fullHeader = `> sudo run portfolio --user="${core.name}"`;

  useEffect(() => {
    let i = 0;
    const interval = setInterval(() => {
      setTypedHeader(fullHeader.substring(0, i));
      i++;
      if (i > fullHeader.length) clearInterval(interval);
    }, 50);
    return () => clearInterval(interval);
  }, [fullHeader]);

  // Map activeColor to a valid tailwind hex or name. For terminal, we just use the class
  const textColorClass = activeColor?.primary || 'text-green-500';

  return (
    <div className="min-h-screen bg-black text-gray-300 font-mono p-4 sm:p-8 text-sm sm:text-base selection:bg-gray-800 selection:text-white">
      <Helmet><title>{core.name} | Terminal</title></Helmet>

      <div className="max-w-4xl mx-auto space-y-8">
        
        {/* Terminal Header */}
        <div className="mb-10">
          <div className="flex gap-2 mb-4">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <h1 className={`text-xl sm:text-2xl font-bold ${textColorClass}`}>
            {typedHeader}<span className="animate-pulse">_</span>
          </h1>
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            transition={{ delay: 1.5 }}
            className="mt-4 text-gray-400"
          >
            [OK] User authenticated.<br/>
            [OK] Loading profile data...<br/><br/>
            <span className="text-white font-bold">NAME:</span> {core.name}<br/>
            <span className="text-white font-bold">EMAIL:</span> {core.email || 'N/A'}<br/>
            <span className="text-white font-bold">STATUS:</span> <span className={textColorClass}>ONLINE</span>
          </motion.div>
        </div>

        {/* Social Links */}
        <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }} className="space-y-2">
          <div className={textColorClass}>~/socials $ ls -l</div>
          <ul className="pl-4 space-y-1">
            {socials.github && <li>-rwxr-xr-x 1 user root <a href={socials.github} className="text-blue-400 hover:underline">github.sh</a></li>}
            {socials.linkedin && <li>-rwxr-xr-x 1 user root <a href={socials.linkedin} className="text-blue-400 hover:underline">linkedin.sh</a></li>}
            {socials.twitter && <li>-rwxr-xr-x 1 user root <a href={socials.twitter} className="text-blue-400 hover:underline">twitter.sh</a></li>}
            {socials.custom && <li>-rwxr-xr-x 1 user root <a href={socials.custom} className="text-blue-400 hover:underline">website.sh</a></li>}
          </ul>
        </motion.section>

        {/* About */}
        {core.summary && (
          <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.5 }} className="space-y-2">
            <div className={textColorClass}>~/about $ cat summary.txt</div>
            <p className="pl-4 text-gray-300 leading-relaxed max-w-2xl">
              {core.summary}
            </p>
          </motion.section>
        )}

        {/* Experience */}
        {experience.length > 0 && (
          <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3 }} className="space-y-4">
            <div className={textColorClass}>~/experience $ ./print_jobs.sh</div>
            <div className="pl-4 border-l-2 border-gray-800 space-y-6">
              {experience.map((exp, idx) => (
                <div key={idx} className="space-y-1">
                  <div className="text-white font-bold">[{exp.start_date} - {exp.end_date || 'Present'}]</div>
                  <div className={`font-semibold ${textColorClass}`}>{exp.job_title} @ {exp.company}</div>
                  <div className="text-gray-400">&gt; {exp.description}</div>
                </div>
              ))}
            </div>
          </motion.section>
        )}

        {/* Projects */}
        {projects.length > 0 && (
          <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3.5 }} className="space-y-4">
            <div className={textColorClass}>~/projects $ ls -1 --color=auto</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pl-4">
              {projects.map((proj, idx) => (
                <div key={idx} className="border border-gray-800 p-4 hover:border-gray-600 transition-colors bg-gray-900/50">
                  <h3 className="text-white font-bold mb-2">./{proj.title.toLowerCase().replace(/\s+/g, '_')}</h3>
                  <p className="text-gray-400 text-sm mb-3">{proj.description}</p>
                  {proj.link && (
                    <a href={proj.link} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline text-sm">
                      [Execute Link]
                    </a>
                  )}
                </div>
              ))}
            </div>
          </motion.section>
        )}

        {/* Skills */}
        {skills.length > 0 && (
          <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 4 }} className="space-y-2 pb-10">
            <div className={textColorClass}>~/skills $ cat package.json | grep "dependencies"</div>
            <div className="pl-4 flex flex-wrap gap-x-4 gap-y-2">
              {skills.map((sk, idx) => (
                <span key={idx} className="text-gray-300">
                  "<span className="text-yellow-300">{sk.name || sk}</span>": "latest"{idx !== skills.length - 1 ? ',' : ''}
                </span>
              ))}
            </div>
          </motion.section>
        )}

        {/* Live Coding Stats */}
        {(socials.github || socials.leetcode) && (
          <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 4.5 }} className="space-y-2 pb-10">
            <div className={textColorClass}>~/stats $ ./fetch_live_stats.sh</div>
            <div className="pl-4">
              <LiveCodingStats githubUrl={socials.github} leetcodeUrl={socials.leetcode} activeColor={activeColor} themeType="dark" />
            </div>
            <div className="mt-8 text-gray-500 italic">~/ $ ▮</div>
          </motion.section>
        )}
        
        {!(socials.github || socials.leetcode) && (
           <div className="mt-8 pb-10 text-gray-500 italic">~/ $ ▮</div>
        )}

      </div>
    </div>
  );
}
